import Dexie, { type Transaction } from "dexie";
import CryptoService, { isEncryptedBlob } from './CryptoService'
import { mergeConfigCompany, CONFIG_DB_NAME, declareConfigSchema, openConfigDb } from '@/core/worker/db-constants.js'

// ── Singleton DB instance ─────────────────────────────────────────────────────
// Created once and reused. The version ladder lives in db-constants.js — it is
// shared with server.worker, DatabaseManager, actionList and activity, which
// MUST all declare exactly the same stores per version.
let _db: Dexie | null = null
let _open: Promise<Dexie> | null = null

function getDB(): Dexie {
  if (!_db) _db = declareConfigSchema(new Dexie(CONFIG_DB_NAME)) as Dexie
  return _db
}

/** Returns the DB, ensuring it is open. Awaitable.
 *  Never bypass this with the raw instance: openConfigDb() first repairs legacy
 *  installs whose stores still carry the old '++id' primary keys, and a Dexie
 *  auto-open racing that repair fails with UpgradeError.
 *
 *  Ein FEHLGESCHLAGENER Open darf nicht gemerkt werden: `_open` wurde bisher
 *  auch im Rejection-Fall behalten, und danach lief jeder weitere Zugriff auf
 *  dieselbe abgelehnte Promise — ein einziger blockierter Upgrade (die
 *  Primärschlüssel-Migration wartet bis zu 10 s auf andere Verbindungen)
 *  machte die Config-DB für die restliche Seitenlebenszeit unlesbar, inklusive
 *  der Login-Zeile. Jetzt wird der Fehler durchgereicht UND der Slot geleert,
 *  sodass der nächste Aufruf es erneut versucht. */
async function db(): Promise<Dexie> {
  if (!_open) {
    _open = openConfigDb(getDB()).catch((err) => {
      _open = null
      throw err
    }) as Promise<Dexie>
  }
  return _open as Promise<Dexie>
}

var standarddbschema = {
  user: 'id, em',
  companies: 'id',
  params: 'param'
}
var standarduserschema = {
  id: '', em: '', pw: '', at: '', rt: '', pk: '', sk: '', cs: '', ac: '', di: '', vs: '', kl: false
  // Note: at/rt are stored encrypted (EncryptedBlob) when CryptoService is ready
}

class DBConfigService {

  public static checkDBSchema(): void {
    // No-op — kept for compatibility
  }

  /** @deprecated — DB is now initialized as a singleton. No need to call manually. */
  public static initSchema(): void { DBConfigService.init() }

  /** Starts the open (and, on legacy installs, the primary-key migration) as
   *  early as possible. Fire-and-forget — every read goes through db() anyway. */
  public static init(): void {
    void db().catch(err => console.error('[DBConfig] open failed:', err))
  }

  // ── Params table helpers ───────────────────────────────────────────────────

  public static async getParam(key: string): Promise<any> {
    const d = await db()
    const result = await d.table('params').get(key)
    return result ? result.value : null
  }

  public static async setParam(key: string, value: any): Promise<void> {
    const d = await db()
    await d.table('params').put({ param: key, value, updated: new Date().toISOString() })
  }

  public static async deleteParam(key: string): Promise<void> {
    const d = await db()
    await d.table('params').delete(key)
  }

  public static async getAllParams(): Promise<any[]> {
    const d = await db()
    return d.table('params').toArray()
  }

  public static async countParams(): Promise<number> {
    const d = await db()
    return d.table('params').count()
  }

  /**
   * "kl" = keep logged in. Historisch als Boolean geschrieben, aber nicht von
   * jedem Schreibpfad und nicht von jedem Build: IndexedDB kennt keinen
   * Boolean-Typzwang, und Zeilen, die über ältere Stände oder über die
   * Primärschlüssel-Migration gelaufen sind, können 1 oder "true" tragen.
   *
   * Der Vergleich stand auf `=== true` und hat solche Zeilen als "nicht
   * angemeldet" verworfen — der Router-Guard leitet dann auf die Loginseite um.
   * Bewusst NICHT einfach truthy geprüft: `kl` ist im abgemeldeten Zustand
   * `false`/`0`, und die dürfen nie durchrutschen.
   */
  private static isKeepLoggedIn(value: unknown): boolean {
    return value === true || value === 1 || value === 'true'
  }

  public static async checkifuserisloggedin(): Promise<any> {
    const results = await DBConfigService.getalluserfromstorage()
      .catch(function (err) {
        console.error('[DBConfig] getalluserfromstorage failed:', err)
        throw err
      })

    let loggedinuser: any = {}
    for (let i = 0; i < results.length; i++) {
      if (DBConfigService.isKeepLoggedIn(results[i].kl)) loggedinuser = results[i]
    }
    if (loggedinuser && loggedinuser.id) return loggedinuser

    // Der Grund MUSS an der Ablehnung hängen. Vorher stand hier ein nacktes
    // `reject()`, und die Diagnose bekam entsprechend eine leere Zeile
    // ("authcheck.failed" ohne Detail) — ausgerechnet die Information, die man
    // an dieser Stelle braucht, ging verloren. Die frühere Zusammenfassung war
    // ein console.log und wird im Produktionsbuild entfernt; deshalb wandert
    // sie in die Fehlermeldung. Nur Form der Werte, keine Mailadressen.
    const shape = results
      .map((r: any) => `kl=${JSON.stringify(r.kl)}/${typeof r.kl}${r.id ? '' : ',no-id'}`)
      .join(' ') || '(keine Zeilen)'
    throw new Error(`no keep-logged-in user row — rows=${results.length}: ${shape}`)
  }
  public static async getalluserfromstorage(): Promise<any[]> {
    let result = await (await db())
      .table('user')
      .toArray()
      .then(function (result) {
        if (result) {
          return result
        }
        return []
      })
    // Transparently decrypt sensitive fields if the crypto key is ready
    if (CryptoService.isReady()) {
      result = await Promise.all(result.map(DBConfigService._decryptUserRow))
    }
    return result
  }
  public static async getuserfromstorage(credentials: any): Promise<any> {
    DBConfigService.initSchema()
    let query: any = ''
    if (credentials.em) query = { 'em': credentials.em.toLowerCase() }
    if (credentials.id) query = { 'id': credentials.id }
    let result = await (await db())
      .table('user')
      .get(query)
      .then(function (result: any) {
        if (result) {
          // result.cs = JSON.parse(result.cs)
          return result
        } else if (!result) {
          return {}
        }
      })
    // Transparently decrypt sensitive fields if the crypto key is ready
    if (result && result.id && CryptoService.isReady()) {
      result = await DBConfigService._decryptUserRow(result)
    }
    return result
  }
  public static async insertnewuser(data) {
    // Encrypt sensitive fields before persisting
    const toStore = CryptoService.isReady()
      ? await DBConfigService._encryptUserRow(data)
      : data
    const result = await (await db())
      .table('user')
      .put(toStore)
      .then(function (result) {
        return result
      })
    return result
  }
  public static async updateuser(data: any): Promise<void> {
    // Muss überarbeitet werden
    await DBConfigService.getuserfromstorage(data)
      .then(async function (result: any) {
        if (!result || !result.id) return   // kein Treffer → nichts zu aktualisieren
        let keys = Object.keys(data);
        for (let i = 0; i < keys.length; i++) {
          let key = keys[i]
          // Bedingung war `data[key] && result[key]` — sie verlangte, dass der
          // VORHANDENE Wert truthy ist. Damit war jedes Feld, das einmal auf
          // '', 0 oder false stand, dauerhaft unveränderbar: genau so blieb
          // `kl: false` über jeden Login hinweg stehen. Der eingehende Wert
          // muss weiterhin truthy sein (leere Serverantworten überschreiben
          // nichts), das Feld muss auf der Zeile nur EXISTIEREN.
          if (data[key] && result[key] !== undefined) {
            result[key] = data[key]
          }
        }
        // getuserfromstorage liefert die Zeile ENTSCHLÜSSELT zurück. Ohne diesen
        // Schritt schrieb .modify() at/rt bei jedem Seitenaufruf im Klartext
        // zurück in die IndexedDB und hob die At-Rest-Verschlüsselung faktisch
        // auf — dieselbe Absicherung, die insertnewuser schon hat.
        const toStore = CryptoService.isReady()
          ? await DBConfigService._encryptUserRow(result)
          : result
        return (await db())
          .table('user')
          .where({ id: result.id })
          .modify(toStore)
      })
      .catch(function (err) {
        console.warn('[DBConfig] updateuser failed:', err)
      })
  }
  /**
   * Setzt das "keep logged in"-Kennzeichen direkt — bewusst NICHT über
   * updateuser().
   *
   * updateuser() führt ein Feld-Merge aus und ist damit die falsche Stelle für
   * eine Zustandsänderung: Es überträgt nur Schlüssel, die die Serverantwort
   * mitbringt, und `kl` bringt sie nie mit. Deshalb konnte eine Zeile, die
   * einmal auf `kl: false` stand, durch keinen Login mehr angemeldet werden —
   * `insertnewuser({… kl: true})` läuft nur bei der Neuanlage, und die fand
   * für eine vorhandene Zeile nie statt. Genau das war der Refresh-Rauswurf.
   *
   * @param id    Nutzer-ID; ohne Angabe werden ALLE Zeilen gesetzt (Logout)
   * @param value true beim Anmelden, false beim Abmelden
   */
  public static setKeepLoggedIn(id: string | null, value: boolean): Promise<void> {
    // Serialisiert, weil die Aufrufe sich sonst überholen: SignIn.vue ruft beim
    // Absenden erst logout() (kl=false) und direkt danach login() → setAuth
    // (kl=true). Beide Schreibvorgänge sind asynchron und werden von niemandem
    // abgewartet; ohne diese Kette könnte das false nach dem true landen und
    // der frisch angemeldete Nutzer wäre wieder abgemeldet. Die Kette hält die
    // Aufrufreihenfolge fest, egal wie die Transaktionen laufen.
    DBConfigService._keepLoggedInQueue = DBConfigService._keepLoggedInQueue
      .then(async () => {
        const table = (await db()).table('user')
        const count = id
          ? await table.where({ id }).modify({ kl: value })
          : await table.toCollection().modify({ kl: value })
        if (!count) console.warn('[DBConfig] setKeepLoggedIn: keine Zeile getroffen für id', id)
      })
      .catch((err) => {
        console.error('[DBConfig] setKeepLoggedIn failed:', err)
      })
    return DBConfigService._keepLoggedInQueue
  }
  private static _keepLoggedInQueue: Promise<void> = Promise.resolve()

  public static async getentriesofcore(table) {
    let result = await (await db())
      .table(table)
      .toArray()
      .then(function (result1) {
        return result1
      })
    return result
  }
  public static async checkifinitialimportdone() {
    let initialimportfromserver = false
    let result = DBConfigService.getentriesofcore('companies')
      .then(function (results) {
        for (let i = 0; i < results.length; i++) {
          if (results[i].ii === true) initialimportfromserver = true
        }
        return initialimportfromserver
      })
    return result
  }

  /** Mark all companies in IndexedDB as initialized (ii = true).
   *  Called after the initial server sync completes so the next app
   *  start performs an incremental sync instead of a full re-sync. */
  public static async markAllCompaniesInitialized(): Promise<void> {
    try {
      const d = await db()
      const companies = await d.table('companies').toArray()
      for (const company of companies) {
        // archived companies are never synced — do not fake ii=true for them
        if (Number(company.archive)) continue
        if (!company.ii) {
          await d.table('companies').put(mergeConfigCompany(company, { ii: true }))
          console.log('[DBConfigService] ii=true saved for company', company.id)
        }
      }
    } catch (err) {
      console.error('[DBConfigService] Failed to mark companies as initialized:', err)
    }
  }
  /** Resolve which company may become the ActiveCompany on startup/restore.
   *  Returns `preferred` unless the config DB marks it as archived — then the
   *  first non-archived candidate wins. An archived company must never be
   *  auto-selected on load; it can still be opened deliberately via the
   *  CompanySwitcher. Companies unknown to the config DB count as active
   *  (fresh device, sync not done yet). */
  public static async resolveActiveCompany(preferred: string, candidates: any): Promise<string> {
    let list: string[] = []
    if (typeof candidates === 'string') {
      try { list = JSON.parse(candidates) } catch { list = [] }
    } else if (Array.isArray(candidates)) {
      list = candidates
    }
    if (!Array.isArray(list)) list = []
    // A company the user no longer has access to must not be restored either.
    if (preferred && list.length > 0 && !list.includes(preferred)) {
      console.log('🏢 Preferred company', preferred, 'is not in the user\'s company list — ignoring')
      preferred = ''
    }
    try {
      const rows = await (await db()).table('companies').toArray()
      const archived = new Set(rows.filter((r: any) => Number(r.archive) === 1).map((r: any) => r.id))
      if (preferred && !archived.has(preferred)) return preferred
      const fallback = list.find(id => id && !archived.has(id))
      if (fallback) {
        if (preferred) console.log('🏢 Preferred company', preferred, 'is archived — falling back to', fallback)
        return fallback
      }
    } catch (err) {
      console.warn('[DBConfigService] resolveActiveCompany failed — keeping preferred:', err)
    }
    return preferred || list[0] || ''
  }

  public static async checkifcompaniesindb(comp: any): Promise<any[]> {
    let companies: any[] = []
    if (typeof comp === 'string') {
      companies = JSON.parse(comp)
    } else {
      companies = [...comp]
    }

    // Check all companies in parallel and collect new ones
    const checks = companies.map(async (companyId) => {
      const result = await (await db()).table('companies').get({ id: companyId })

      if (!result) {
        // Company doesn't exist - create it (canonical shape via mergeConfigCompany)
        let soy = new Date(new Date().getFullYear(), 0, 1)
        let eoy = new Date(new Date().getFullYear(), 11, 31)
        const entry = mergeConfigCompany(null, {
          id: companyId,
          datestart: soy.formatDate('YYYY-MM-DD HH:mm:ss'),
          dateend: eoy.formatDate('YYYY-MM-DD HH:mm:ss'),
          lastupdate: new Date().formatDate('YYYY-MM-DD HH:mm:ss'),
          ii: false,
          archive: 0
        })
        await (await db()).table('companies').put(entry)
        console.log('✨ New company detected and added to IndexedDB:', companyId)
        return companyId // Return company ID for new companies
      }
      // Existing rows were historically written in wildly different shapes
      // (raw mycrogym_core company row vs. sync-state stub) — normalize them
      // to the canonical shape so all browsers/users see identical records.
      const normalized = mergeConfigCompany(result, {})
      if (JSON.stringify(normalized) !== JSON.stringify(result)) {
        await (await db()).table('companies').put(normalized)
        console.log('🧹 Normalized config-DB company record:', companyId)
      }
      return null // Return null for existing companies
    })

    const results = await Promise.all(checks)
    const newCompanies = results.filter(id => id !== null)

    return newCompanies // Return array of new company IDs
  }

  /**
   * Mark every company in the config DB as "not initially imported" so the next
   * sync runs a FULL initial import instead of an incremental window.
   *
   * Why this exists: `ii: true` is the persistent claim "this browser holds a
   * complete data set for that company". Whenever the local data set is thrown
   * away or becomes untrustworthy — a re-registered device, an explicit full
   * sync — that claim is false, and leaving it standing is the single most
   * expensive failure mode this app has: the worker then only ever requests an
   * incremental window, so the app stays EMPTY without a single error in the
   * console (see LEARNINGS 2026-08-15).
   *
   * The three timestamps are cleared along with the flag. A row with ii:false
   * but a leftover datestart/dateend/lastupdate would otherwise let a later
   * code path reconstruct an incremental window from stale bounds.
   *
   * Clearing the flag FIRST (before the worker is asked to re-sync) is what
   * makes an interrupted re-sync safe: if the user closes the tab midway, the
   * persisted state still says "not initialized" and the next start retries.
   *
   * @returns number of company rows that were reset
   */
  public static async resetCompaniesForFullSync(): Promise<number> {
    const table = (await db()).table('companies')
    const rows = await table.toArray()
    if (!rows || rows.length === 0) return 0
    await table.bulkPut(rows.map((row: any) => mergeConfigCompany(row, {
      ii: false,
      ii_co: false,
      datestart: '',
      dateend: '',
      lastupdate: ''
    })))
    console.log(`🔄 ${rows.length} company/companies marked for a full initial sync`)
    return rows.length
  }


  // ── Integrations table ────────────────────────────────────────────────────────
  // Stores third-party service credentials locally (never sent to the server).
  // Key format convention:  '<provider>.<name>'  e.g. 'email.gmail', 'bank.finapi'
  //
  // Stored shape:
  // {
  //   key:       string          – primary key, unique per integration config
  //   provider:  string          – provider type: 'email' | 'bank' | ...
  //   companyid: string          – which company this config belongs to
  //   config:    object          – provider-specific settings (see IntegrationsService)
  //   enabled:   boolean
  //   updated:   ISO date string
  // }

  public static async getIntegration(key: string): Promise<any | null> {
    const d = await db()
    const row = await d.table('integrations').get(key) ?? null
    if (!row) return null
    return DBConfigService._decryptIntegrationRow(row)
  }

  public static async getIntegrationsByProvider(provider: string, companyid: string): Promise<any[]> {
    const d = await db()
    const rows = await d.table('integrations').where({ provider }).filter((r: any) => String(r.companyid) === String(companyid)).toArray()
    return Promise.all(rows.map(DBConfigService._decryptIntegrationRow))
  }

  public static async getAllIntegrations(companyid?: string): Promise<any[]> {
    const d = await db()
    const all = await d.table('integrations').toArray()
    const filtered = companyid ? all.filter((r: any) => r.companyid === companyid) : all
    return Promise.all(filtered.map(DBConfigService._decryptIntegrationRow))
  }

  public static async setIntegration(
    key: string,
    provider: string,
    companyid: string,
    config: Record<string, any>,
    enabled = true
  ): Promise<void> {
    const d = await db()
    // Encrypt the config object before storing
    const encryptedConfig = CryptoService.isReady()
      ? await CryptoService.encrypt(config)
      : config
    await d.table('integrations').put({
      key,
      provider,
      companyid,
      config: encryptedConfig,
      enabled,
      updated: new Date().toISOString()
    })
  }

  public static async deleteIntegration(key: string): Promise<void> {
    const d = await db()
    await d.table('integrations').delete(key)
  }

  // User view settings methods for activesettingsperview
  /**
   * Get user view settings from IndexedDB
   * @param userId - User ID to retrieve settings for
   * @returns Parsed settings object or empty object if none exist
   */
  public static async getUserViewSettings(userId: string): Promise<any> {
    DBConfigService.initSchema()
    const user = await (await db())
      .table('user')
      .get({ id: userId })

    if (user && user.vs) {
      try {
        // vs field may be stored as string or object
        return typeof user.vs === 'string' ? JSON.parse(user.vs) : user.vs
      } catch (error) {
        console.error('Failed to parse user view settings:', error)
        return {}
      }
    }
    return {}
  }

  /**
   * Update user view settings in IndexedDB
   * @param userId - User ID to update settings for
   * @param settings - Settings object to save
   */
  public static async updateUserViewSettings(userId: string, settings: any): Promise<void> {
    DBConfigService.initSchema()
    await (await db())
      .table('user')
      .where({ id: userId })
      .modify({
        vs: typeof settings === 'object' ? JSON.stringify(settings) : settings
      })
  }


  // ── Crypto helpers (private) ─────────────────────────────────────────────────

  /**
   * Fields in the user row that contain sensitive tokens and are encrypted at rest.
   * Index fields (id, em) MUST stay plaintext for Dexie queries to work.
   */
  private static ENCRYPTED_USER_FIELDS = ['at', 'rt'] as const

  /**
   * Encrypt sensitive fields in a user row before writing to IndexedDB.
   * Non-sensitive fields (id, em, kl, di, cs, …) are left as-is.
   */
  private static async _encryptUserRow(user: any): Promise<any> {
    if (!CryptoService.isReady() || !user) return user
    const copy = { ...user }
    for (const field of DBConfigService.ENCRYPTED_USER_FIELDS) {
      if (copy[field] && !isEncryptedBlob(copy[field])) {
        copy[field] = await CryptoService.encrypt(copy[field])
      }
    }
    return copy
  }

  /**
   * Decrypt sensitive fields in a user row after reading from IndexedDB.
   * If a field is plaintext (migration case), it is returned as-is and
   * will be re-encrypted on the next write.
   */
  private static async _decryptUserRow(user: any): Promise<any> {
    if (!CryptoService.isReady() || !user) return user
    const copy = { ...user }
    for (const field of DBConfigService.ENCRYPTED_USER_FIELDS) {
      if (isEncryptedBlob(copy[field])) {
        try {
          copy[field] = await CryptoService.decrypt(copy[field])
        } catch (err) {
          console.warn(`[DBConfigService] Failed to decrypt user.${field} — leaving as-is`, err)
        }
      }
      // If it's a plain string (pre-migration), leave it — will be encrypted on next write
    }
    return copy
  }

  /**
   * Decrypt the config blob of an integration row.
   * Handles both encrypted blobs (post-migration) and plain objects (pre-migration).
   */
  private static async _decryptIntegrationRow(row: any): Promise<any> {
    if (!row) return row
    if (!CryptoService.isReady()) return row
    if (!isEncryptedBlob(row.config)) return row // pre-migration plaintext config
    try {
      const config = await CryptoService.decrypt(row.config)
      return { ...row, config }
    } catch (err) {
      console.warn('[DBConfigService] Failed to decrypt integration.config — returning row with encrypted blob', err)
      return row
    }
  }



  /**
   * Initialize CryptoService from the device's RSA private key (sk field).
   *
   * Accepts either a full user row or a credentials object (which may carry
   * `privatekey` for a freshly-generated keypair, or `sk` from an existing row).
   *
   * Fallback chain (in order):
   *   1. user.sk        — RSA private key PEM stored in user table (device-unique)
   *   2. user.privatekey — freshly-generated RSA key on first login of a new device
   *   3. params['device_secret'] — random secret from previous approach (backwards compat)
   *   4. Generate a new random device_secret and persist it (last resort)
   *
   * Idempotent — safe to call multiple times.
   */
  public static async initCryptoFromUser(_user?: any): Promise<void> {
    if (CryptoService.isReady()) return

    // Primary: use the device_secret stored in params table (32 random bytes, base64).
    // This is the correct seed format for CryptoService.init() (which calls base64ToBytes).
    const existing = await DBConfigService.getParam('device_secret')
    if (existing) {
      await CryptoService.init(existing)
      return
    }

    // No device_secret yet (first login on this device) — generate and persist one.
    // The RSA sk field is NOT used as seed because it is a PEM string, not base64 bytes.
    const secret = CryptoService.generateDeviceSecret()
    await DBConfigService.setParam('device_secret', secret)
    await CryptoService.init(secret)
  }

  /**
   * Bootstrap CryptoService on page refresh, before any encrypted DB reads.
   *
   * Reads the raw user table WITHOUT decryption (sk is never encrypted, so
   * there is no chicken-and-egg problem) to find the logged-in user's sk,
   * then calls initCryptoFromUser().
   *
   * Must be called before checkifuserisloggedin() / getalluserfromstorage().
   */
  public static async bootstrapCrypto(): Promise<void> {
    if (CryptoService.isReady()) return
    // Delegates to initCryptoFromUser which reads device_secret from params table.
    await DBConfigService.initCryptoFromUser()
  }

}

export default DBConfigService;
